<template>
    <div class="poll-rule">
        <resource-select
            :value="guide"
            :options="guides"
            :loading="loadingResources"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            field="guideId"
            :placeholder="$t('pollRule.Select Poll')"
            @input="$emit('input', $event)">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="guide">
            <simple-select
                v-if="showNpsSegmentOptionSelect"
                :options="npsSegmentOptions"
                :value="npsSegmentOption"
                :check-valid-rule="checkValidRule"
                field="npsSegmentType"
                :placeholder="$t('pollRule.Filter segment by')"
                @input="$emit('input', $event)" />

            <template v-if="showNpsThemesListSelect">
                <simple-select
                    :options="npsThemesListOptions"
                    :value="npsThemeOption"
                    :check-valid-rule="checkValidRule"
                    field="value"
                    :placeholder="$t('pollRule.Select Theme')"
                    :disabled="npsThemesListOptions.length === 0"
                    :loading-resources="loadingThemesList"
                    @input="$emit('input', $event)" />
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Condition') }}</strong>
                    <div class="flex-break" />
                </template>
                <simple-select
                    :options="npsResponseGroupOptions"
                    :value="npsResponseGroupOption"
                    :check-valid-rule="checkValidRule"
                    preselect-first
                    field="npsResponseGroup"
                    @input="$emit('input', $event)" />
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Criteria') }}</strong>
                </template>
            </template>
            <template v-if="showPollQuestionsControls">
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Condition') }}</strong>
                    <div class="flex-break" />
                </template>
                <simple-select
                    :options="pollQuestions"
                    :value="pollId"
                    :disabled="readOnly"
                    :check-valid-rule="checkValidRule"
                    preselect-first
                    field="pollId"
                    :placeholder="$t('pollRule.Select Poll Question')"
                    @input="$emit('input', $event)" />
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Criteria') }}</strong>
                    <div class="flex-break" />
                </template>
                <simple-select
                    :options="pollOperators"
                    :value="operatorValue"
                    :disabled="readOnly"
                    :check-valid-rule="checkValidRule"
                    preselect-first
                    field="operator"
                    @input="$emit('input', $event)" />
                <template v-if="showValueComponent">
                    <component
                        :is="valueComponent.component"
                        v-bind="valueComponent.props"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        @input="$emit('input', $event)" />
                </template>
            </template>
            <template v-if="showDateControls">
                <div
                    v-if="!isLocaleEnglish"
                    class="flex-break" />
                <simple-select
                    :options="timeOptions"
                    :value="timeValue"
                    :disabled="readOnly"
                    :check-valid-rule="checkValidRule"
                    preselect-first
                    field="time"
                    @input="$emit('input', $event)" />
                <component
                    :is="dateComponent.component"
                    v-if="dateComponent"
                    v-bind="dateComponent.props"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="$emit('input', $event)" />
            </template>
        </template>
    </div>
</template>

<script>
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import StringInput from '@/segments/components/segment-editor-vue/components/string-input.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import { getAllNpsThemes, getThemeDisplayName } from '@/net-promoter-system/nps-responses-table/utils';
import { mapGetters } from 'vuex';
import get from 'lodash/get';

import {
    getPolls,
    getTimeOptions,
    getNumberConfig,
    getStringConfig,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'PollRule',
    components: {
        ResourceSelect,
        SingleDate,
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
        StringInput,
        SimpleSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input', 'time-rule-changed'],
    data () {
        return {
            hasNpsThemes: false,
            npsSegmentOptions: [],
            npsResponseGroupOptions: [],
            loadingThemesList: false,
            timeOptions: [],
            allNpsThemes: [],
        };
    },
    computed: {
        ...mapGetters({
            getFeatureFlag: 'subscription/getFeatureFlag',
        }),
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp, resourceType: 'poll' });
        },
        guides () {
            return this.resources.reduce((acc, guide) => {
                const isResourceCenter = get(guide, 'attributes.resourceCenter') && guide.state !== 'public';
                const isSurveyGuide = get(guide, 'attributes.surveyId');
                const polls = getPolls(guide);
                if (isResourceCenter || isSurveyGuide || !polls.length) {
                    return acc;
                }

                acc.push({
                    guideId: guide.id,
                    name: guide.name,
                    polls,
                    state: guide.state,
                });

                return acc;
            }, []);
        },
        guide () {
            return this.guides.find((guide) => guide.guideId === this.rule.guideId);
        },
        isNpsGuide () {
            return get(this.guide, 'polls[0].type') === 'npsrating';
        },
        npsSegmentOption () {
            if (this.rule.npsSegmentType) {
                return this.npsSegmentOptions.find((o) => o.npsSegmentType === get(this.rule, 'npsSegmentType'));
            }
            /* npsSegmentType is undefind */
            if (this.operatorValue) {
                /* editing segment which created before npsSegmentType added */
                this.$emit('input', { field: 'npsSegmentType', value: 'npsPollSteps' });

                return this.npsSegmentOptions.find((o) => o.npsSegmentType === 'npsPollSteps');
            }

            /* creating new segment */
            return null;
        },
        showNpsSegmentOptionSelect () {
            return this.isNpsGuide && this.hasNpsThemes;
        },
        npsThemesListOptions () {
            return this.allNpsThemes.map((t) => ({ value: t.name, name: getThemeDisplayName(t) }));
        },
        showNpsThemesListSelect () {
            return this.showNpsSegmentOptionSelect && get(this.rule, 'npsSegmentType') === 'npsThemes';
        },
        npsThemeOption () {
            if (this.showNpsThemesListSelect) {
                const theme = get(this.rule, 'value') || '';

                return this.npsThemesListOptions.find((o) => o.value === theme);
            }

            return null;
        },
        showPollQuestionsControls () {
            return !this.showNpsSegmentOptionSelect || get(this.npsSegmentOption, 'npsSegmentType') === 'npsPollSteps';
        },
        npsResponseGroupOption () {
            const npsResponseGroup = get(this.rule, 'npsResponseGroup');
            if (!npsResponseGroup) {
                return this.npsResponseGroupOptions.find((o) => o.npsResponseGroup === 'allResponseGroups');
            }

            return this.npsResponseGroupOptions.find((o) => o.npsResponseGroup === npsResponseGroup);
        },
        showDateControls () {
            return !this.hasNpsThemes || !this.isNpsGuide || this.npsSegmentOption;
        },
        pollQuestions () {
            return this.guide.polls;
        },
        pollId () {
            return this.pollQuestions.find((question) => question.pollId === this.rule.pollId);
        },
        pollOperators () {
            if (!this.pollId) {
                return [];
            }

            let options;
            const equalToOptions = [
                { operator: '==', name: this.$t('pollRule.is equal to') },
                { operator: '!=', name: this.$t('pollRule.is not equal to') },
            ];

            const respondedOptions = [
                { operator: 'responded', name: this.$t('pollRule.responded') },
                { operator: '!responded', name: this.$t('pollRule.not responded') },
            ];

            switch (this.pollId.type) {
                case 'freeform':
                case 'picklist':
                case 'npsreason':
                    options = [
                        { operator: 'contains', name: this.$t('pollRule.contains') },
                        { operator: '!contains', name: this.$t('pollRule.does not contain') },
                    ];
                    break;
                case 'npsrating':
                case 'npsscore':
                case 'numberscale':
                    options = [
                        { operator: '<=', name: this.$t('pollRule.less or equal to') },
                        { operator: '>=', name: this.$t('pollRule.greater or equal to') },
                    ];
                    break;
                default:
                    options = [];
                    break;
            }

            return [...equalToOptions, ...options, ...respondedOptions];
        },
        operatorValue () {
            return this.pollOperators.find((operator) => operator.operator === this.rule.operator);
        },
        pollValues () {
            if (!this.pollId) {
                return [];
            }

            return this.pollId.options;
        },
        showValueComponent () {
            if (this.rule.operator) {
                return !['responded', '!responded'].includes(this.rule.operator);
            }

            return false;
        },
        valueComponent () {
            if (this.pollValues && this.pollValues.length) {
                return {
                    component: 'simple-select',
                    props: {
                        value: this.value,
                        field: 'value',
                        options: this.pollValues,
                        preselectFirst: true,
                        searchable: false,
                        disabled: this.readOnly,
                    },
                };
            }

            if (this.pollId && ['freeform', 'npsreason'].includes(this.pollId.type)) {
                return getStringConfig(this.rule, 'value');
            }

            return getNumberConfig(this.rule, 'value');
        },
        value () {
            if (this.pollValues) {
                return this.pollValues.find((value) => value.value === this.rule.value);
            }

            return this.rule.value;
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        dateComponent () {
            switch (this.rule.time) {
                case 'withinLast':
                case '!withinLast':
                case 'withinNext':
                case '!withinNext':
                    return getRelativeDateRangeConfig(this.rule);
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                case '>=':
                case '<=':
                    return getSingleDateConfig(this.rule, 'first');
                default:
                    return false;
            }
        },
    },
    watch: {
        guide (newValue, oldValue) {
            if (newValue.guideId !== (oldValue && oldValue.guideId) && this.isNpsGuide) {
                if (this.hasNpsThemes && this.isNpsGuide) {
                    this.loadThemes();
                }

                const npsRatingPollId = this.pollQuestions.find((poll) => poll.type === 'npsrating').pollId;
                this.$emit('input', { field: 'pollId', value: npsRatingPollId });
                this.$emit('input', { field: 'value', value: null });
            }
        },
        showNpsSegmentOptionSelect () {
            this.$emit('input', { field: 'npsSegmentType', value: null });
        },
        showNpsThemesListSelect (newValue) {
            /* When user select Filter segment by `NPS Themes` */
            /* Reset theme (value). set operator to `contains`  */
            if (newValue) {
                this.$emit('input', { field: 'value', value: null });
                this.$emit('input', { field: 'operator', value: 'contains' });
            }
        },
        timeValue (newValue) {
            if (newValue) {
                this.$emit('time-rule-changed', newValue);
            }
        },
    },
    created () {
        this.npsSegmentOptions = [
            { npsSegmentType: 'npsPollSteps', name: this.$t('pollRule.NPS Poll Steps') },
            { npsSegmentType: 'npsThemes', name: this.$t('pollRule.NPS Themes'), disabled: true },
        ];

        this.npsResponseGroupOptions = [
            { npsResponseGroup: 'allResponseGroups', name: this.$t('pollRule.All Response Groups') },
            { npsResponseGroup: 'promoters', name: this.$t('pollRule.Promoters') },
            { npsResponseGroup: 'detractors', name: this.$t('pollRule.Detractors') },
            { npsResponseGroup: 'passives', name: this.$t('pollRule.Passives') },
        ];

        this.timeOptions = getTimeOptions().poll;
        this.hasNpsThemes = this.getFeatureFlag('enableNPSThemesTriage');
        if (this.hasNpsThemes && this.isNpsGuide) {
            this.loadThemes();
        }
    },
    methods: {
        async loadThemes () {
            this.loadingThemesList = true;
            this.allNpsThemes = await getAllNpsThemes(this.guide.guideId);
            const allNpsThemesLength = this.allNpsThemes.length;
            this.npsSegmentOptions.find((o) => o.npsSegmentType === 'npsThemes').disabled = !allNpsThemesLength;
            if (allNpsThemesLength === 0) {
                this.$emit('input', { field: 'npsSegmentType', value: null });
            }
            this.loadingThemesList = false;
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
